import { Box, Button, Divider, Grid } from '@mui/material'
import React, { useEffect, useState } from 'react'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import DataRow from './DataRow'
import { useDeleteUserMutation, useGetUsersListQuery } from 'src/api/usersApi'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import SimpleDropdown from 'src/Component/SimpleDropdown'
import ViewUserDetails from './ViewUserDetails'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import { errorToast, successToast } from 'src/utils/toastService'
import SearchInput from 'src/Component/SearchInput'
import FilterDropdown from 'src/Component/FilterDropdown'

const headCells: HeadCellI[] = [
  {
    id: 'id',
    isCheckBox: true,
    sortable: false,
    label: 'checkbox',
  },
  {
    id: 'name',
    sortable: true,
    label: 'User Name',
  },
  {
    id: 'email',
    sortable: true,
    label: 'User Email',
  },
  {
    id: 'user_type',
    sortable: true,
    label: 'User Type',
  },
  {
    id: 'id',
    sortable: false,
    label: 'Action',
    align: 'center',
  },
]

type UserTypeFilterI = 'User' | 'Trainer' | ''
const Users = () => {
  // states
  const [page, setPage] = useState<number>(1)
  const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
  const [sortOrderBy, setSortOrderBy] = useState<string>('') // headCells[i].id
  const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilterI>('')
  const [userDetailsModal, setUserDetailsModal] = useState({
    id: 0,
    open: false,
  })

  const [userDeleteConfirmation, setUserDeleteConfirmation] = useState({
    id: 0,
    open: false,
  })

  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [search, setSearch] = useState<string>('')

  // hooks
  const {
    data: USERS_DATA_RESPONSE,
    isFetching,
    refetch,
  } = useGetUsersListQuery(
    {
      search,
      page,
      pageSize: 10,
      userTypeFilter,
      sortBy: (() => {
        if (sortOrder === 'asc') {
          return sortOrderBy
        } else {
          return `-${sortOrderBy}`
        }
      })(),
    },
    { refetchOnMountOrArgChange: true },
  )
  const [deleteUser, { isLoading }] = useDeleteUserMutation()
  const dispatch = useDispatch<AppDispatch>()

  // functions
  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handleSorting = (_sortOrderBy: string) => {
    setSortOrder((prevState) => {
      return prevState === 'asc' ? 'desc' : 'asc'
    })
    setSortOrderBy(_sortOrderBy)
  }

  const handleUserDetailsModalClose = () => {
    setUserDetailsModal({
      id: 0,
      open: false,
    })
  }

  const handleViewDetailsClick = (id: number) => {
    setUserDetailsModal({
      id: id,
      open: true,
    })
  }

  const handleCloseDeleteConfirmation = () => {
    setUserDeleteConfirmation({
      id: 0,
      open: false,
    })
  }

  const handleOpenDeleteConfirmation = (id: number) => {
    setUserDeleteConfirmation({
      id: id,
      open: true,
    })
  }

  const handleDeleteUser = () => {
    deleteUser(userDeleteConfirmation.id)
      .unwrap()
      .then((res) => {
        console.log(res)
        successToast('User deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast('Unable to delete user.')
      })
      .finally(() => {
        handleCloseDeleteConfirmation()
      })
  }

  const handleSelectUser = (id: number) => {
    if (selectedUserIds.includes(id)) {
      setSelectedUserIds((prevState) => {
        return prevState.filter((userId) => userId !== id)
      })
    } else {
      setSelectedUserIds((prevState) => {
        return [...prevState, id]
      })
    }
  }

  const handleSelectAllClick = (checked: boolean) => {
    if (checked && USERS_DATA_RESPONSE?.results.length) {
      setSelectedUserIds(USERS_DATA_RESPONSE.results.map((user) => user.id))
    } else {
      setSelectedUserIds([])
    }
  }

  const handleDeleteSelected = () => {
    console.log(selectedUserIds)
    Promise.all(selectedUserIds.map((id) => deleteUser(id).unwrap()))
      .then((res) => {
        console.log(res)
        successToast('Selected users deleted successfully.')
        refetch()
      })
      .catch((err) => {
        console.log(err)
        errorToast(`Unable to delete selected ${selectedUserIds.length > 1 ? 'users' : 'user'}.`)
      })
  }

  const handleSearch = (value: string) => {
    setPage(1)
    setSearch(value)
  }

  const handleFilterChange = (e: any) => {
    console.log(e)
    if (e.target) {
      setUserTypeFilter(e.target.value)
      setPage(1)
    }
  }

  const getToolbarFilter = () => {
    type OptionsI = {
      value: UserTypeFilterI
      label: string
    }[]

    const options: OptionsI = [
      { value: '', label: 'All' },
      { value: 'User', label: 'User' },
      { value: 'Trainer', label: 'Trainer' },
    ]
    return (
      <Box pr={5}>
        <Divider />
        <FilterDropdown
          label='Filter by'
          options={options}
          onChange={handleFilterChange}
          value={userTypeFilter}
        />
      </Box>
    )
  }

  // use effects
  useEffect(() => {
    if (isFetching || isLoading) {
      dispatch(startPageLoader())
    } else {
      dispatch(stopPageLoader())
    }
  }, [isFetching, isLoading])

  useEffect(() => {
    setSelectedUserIds([])
  }, [USERS_DATA_RESPONSE])

  console.log(USERS_DATA_RESPONSE, 'userList')
  return (
    <Box className='pageContainer'>
      <Grid container>
        <Grid md={8} sm={12} xs={12}>
          {' '}
        </Grid>
        <Grid md={4} sm={12} xs={12}>
          <Box>
            <SearchInput hasDebounce onChange={handleSearch} />
          </Box>
        </Grid>
      </Grid>

      <Box mt={2}>
        {USERS_DATA_RESPONSE && (
          <PaginatedTable
            title='Users'
            headCells={headCells}
            currentPage={page}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(USERS_DATA_RESPONSE?.count / 10)}
            onSortClick={handleSorting}
            onSelectAllClick={handleSelectAllClick}
            isAllSelected={USERS_DATA_RESPONSE.results.length === selectedUserIds.length}
            numSelected={selectedUserIds.length}
            onDeleteSelected={handleDeleteSelected}
            toolbarFilter={getToolbarFilter()}
          >
            <>
              {USERS_DATA_RESPONSE?.results?.length === 0 && (
                <EmptyTableRow cellCount={headCells.length} />
              )}
              {USERS_DATA_RESPONSE?.results?.map((user) => {
                return (
                  <DataRow
                    key={user.id}
                    id={user.id}
                    name={user.username}
                    email={user.email}
                    userType={user?.user_type?.name || ''}
                    selected={selectedUserIds.includes(user.id)}
                    onViewDetailsClick={handleViewDetailsClick}
                    onDeleteClick={handleOpenDeleteConfirmation}
                    onSelect={handleSelectUser}
                  />
                )
              })}
            </>
          </PaginatedTable>
        )}
      </Box>

      {!!userDetailsModal.id && (
        <ViewUserDetails
          id={userDetailsModal.id}
          open={userDetailsModal.open}
          onClose={handleUserDetailsModalClose}
        />
      )}

      <ConfirmationBox
        title='Are you sure you want to Delete this user.'
        open={userDeleteConfirmation.open}
        onReject={handleCloseDeleteConfirmation}
        onAccept={handleDeleteUser}
      />
    </Box>
  )
}

export default Users
